"use client";

import ChevronDownIcon from "@repo/ui-system/icons/arrows/chevron-down/outline.svg";
import CheckIcon from "@repo/ui-system/icons/buttons/check/filled.svg";
import ShieldCheckIcon from "@repo/ui-system/icons/buttons/shield-v/outline.svg";
import BubbleChatIcon from "@repo/ui-system/icons/random/bubble-chat/outline.svg";
import SmartBrainIcon from "@repo/ui-system/icons/random/smart-brain/outline.svg";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { withBasePath } from "@/lib/base-path";
import { cn } from "@/lib/utils";

const content = {
  title: "How do we rank?",
  items: [
    {
      icon: SmartBrainIcon,
      title: "Powerful technology",
      description:
        "We scan thousands of products and stores, analyzing reviews to find what matters most to users like you",
    },
    {
      icon: BubbleChatIcon,
      title: "Careful review",
      description:
        "Our team ensures quality, value, and fit based on real user feedback",
    },
    {
      icon: ShieldCheckIcon,
      title: "Trusted insights",
      description:
        "AI and human insights guide your decisions, reflecting user priorities",
    },
  ],
  suffix: {
    title: "Real users impact",
    items: [
      "Trusted by thousands daily",
      "Backed by millions of reviews",
      "Shoppers influence our rankings ",
    ],
  },
};

type Props = {
  className?: string;
};

export function HowWeRankCollapsible({ className }: Props) {
  return (
    <Collapsible
      className={cn(
        "rounded-lg border border-[#AEC1FF] overflow-hidden bg-[#F5F7FF]",
        className,
      )}
    >
      <CollapsibleTrigger className="group p-4 w-full">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={withBasePath("/how-we-rank.svg")}
          aria-hidden="true"
          className="w-full h-[112px] object-cover object-bottom mb-3"
        />
        <div className="grid grid-cols-[1fr_auto] justify-between items-center">
          <h3 className="text-start text-text-grayscale-subtitle">
            {content.title}
          </h3>
          <ChevronDownIcon className="size-5 justify-self-end transition-transform group-data-[state=open]:rotate-180" />
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent className="p-4 bg-surface-grayscale-subtle border-t border-border-primary-subtle overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up ">
        <ul className="flex flex-col gap-3">
          {content.items.map((item, idx) => {
            const Icon = item.icon;
            return (
              <li
                key={idx}
                className="flex gap-2"
              >
                <Icon
                  aria-hidden="true"
                  className="size-7 shrink-0 bg-surface-primary-lighter rounded p-1 text-elements-primary-darker"
                />
                <div>
                  <h4 className="mini1 text-text-grayscale-caption">
                    {item.title}
                  </h4>
                  <p className="body1 text-text-grayscale-body">
                    {item.description}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
        <div className="mt-4 flex flex-col gap-2">
          <h4 className="sub1 text-text-grayscale-subtitle">
            {content.suffix.title}
          </h4>
          <ul className="flex flex-col gap-2">
            {content.suffix.items.map((item, idx) => (
              <li
                key={idx}
                className="body1 text-text-grayscale-body flex items-center gap-2"
              >
                <CheckIcon
                  aria-label="Check"
                  className="size-4 text-elements-tertiary-default-2"
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
